import { apiService } from './api';

/**
 * Frontend client for the QC Interne controller (instrument-level QC).
 *
 * Backend exposes:
 *   GET  /qc-internal/runs       → list runs (filters: laboratory, from, to)
 *   GET  /qc-internal/runs/:id   → run + every QC point in CSV order
 *
 * This data is NEVER visible to client-portal users. The frontend route is
 * gated by RoleGuard (admin / manager / analyst), and the backend controller
 * additionally enforces the role check.
 */

export type InstrumentQcCategory =
  | 'blank'
  | 'standard'
  | 'solution_blank'
  | 'crm'
  | 'duplicate';

export interface InstrumentQcRun {
  id: string;
  tenant_id?: string | null;
  file_hash: string;
  filename: string;
  laboratory: string;
  analysis_type?: string | null;
  run_date?: string | null;
  imported_at: string;
  imported_by?: string | null;
  total_blanks: number;
  total_standards: number;
  total_crms: number;
  total_samples: number;
  lots: string[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface InstrumentQcPoint {
  id: string;
  run_id: string;
  category: InstrumentQcCategory;
  label: string;
  csv_type?: string | null;
  measured_at?: string | null;
  position_in_run: number;
  element: string;
  wavelength?: string | null;
  concentration?: number | null;
  unit?: string | null;
  concentration_ppm?: number | null;
  expected_value?: number | null;
  expected_unit?: string | null;
  recovery_percent?: number | null;
  reference_material_id?: string | null;
  flags?: string | null;
  created_at: string;
}

export interface InstrumentQcRunListResponse {
  items: InstrumentQcRun[];
  total: number;
}

export interface InstrumentQcRunDetail {
  run: InstrumentQcRun;
  points: InstrumentQcPoint[];
}

class QcInternalService {
  private base = '/qc-internal';

  async listRuns(filters: {
    laboratory?: string;
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
  } = {}): Promise<InstrumentQcRunListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return apiService.get<InstrumentQcRunListResponse>(
      qs ? `${this.base}/runs?${qs}` : `${this.base}/runs`,
    );
  }

  async getRun(id: string): Promise<InstrumentQcRunDetail> {
    return apiService.get<InstrumentQcRunDetail>(
      `${this.base}/runs/${encodeURIComponent(id)}`,
    );
  }
}

export const qcInternalService = new QcInternalService();
export default qcInternalService;
